import { useTranslation } from "react-i18next";

import { ArrowRightIcon, CornerLeftUpIcon } from "lucide-react";

import { useTheme } from "simplydo/react/theme";
import { H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { Popup } from "simplydo/react/ui/popup";

import {
  annualImpactValue,
  generateImpactText,
  getImpactIconName,
  useImpactStageMap,
} from "../Ideas/ImpactStatements/statementUtils";
import { type IdeaImpactDisplayMeasure, type IdeaImpactsEntry } from "./types";

type LaneCardImpactsProps = {
  showUserEstimate: boolean;
  impactData: IdeaImpactsEntry | undefined;
};

export const LaneCardImpacts = ({
  showUserEstimate,
  impactData,
}: LaneCardImpactsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const stageMap = useImpactStageMap();

  const allImpacts = impactData?.totalImpacts ?? [];
  const pinnedImpacts = allImpacts.filter((i) => i.isPinned);
  const hiddenImpactsCount =
    (allImpacts?.length || 0) - (pinnedImpacts?.length || 0);

  const hasUserEstimate = impactData?.completedImpactStages?.userEstimate;
  const hasProjectEstimate = impactData?.completedImpactStages?.projectEstimate;
  const hasFinalOutcome = impactData?.completedImpactStages?.finalOutcome;

  const currentStage = hasFinalOutcome
    ? "finalOutcome"
    : hasProjectEstimate
      ? "projectEstimate"
      : hasUserEstimate
        ? "userEstimate"
        : null;

  if (!currentStage) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Popup
          trigger={
            <div style={{ display: "flex", gap: 2 }}>
              {showUserEstimate ? (
                <>
                  <Icon
                    icon={stageMap.userEstimate.icon}
                    style={{
                      opacity:
                        hasUserEstimate || hasProjectEstimate || hasFinalOutcome
                          ? 1
                          : 0.3,
                    }}
                  />
                  <Icon
                    icon={ArrowRightIcon}
                    style={{
                      opacity: hasProjectEstimate || hasFinalOutcome ? 1 : 0.3,
                    }}
                  />
                </>
              ) : null}
              <Icon
                icon={stageMap.projectEstimate.icon}
                style={{
                  opacity: hasProjectEstimate || hasFinalOutcome ? 1 : 0.3,
                }}
              />
              <Icon
                icon={ArrowRightIcon}
                style={{ opacity: hasFinalOutcome ? 1 : 0.3 }}
              />
              <Icon
                icon={stageMap.finalOutcome.icon}
                style={{ opacity: hasFinalOutcome ? 1 : 0.3 }}
              />
            </div>
          }
          position="top left"
          size="small"
        >
          {
            <div>
              <p style={{ fontWeight: "bold" }}>
                {stageMap[currentStage]?.name}
              </p>
              {stageMap[currentStage]?.description}
            </div>
          }
        </Popup>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0px",
          }}
        >
          {hiddenImpactsCount > 0 ? (
            <Popup
              hoverable
              trigger={
                <button
                  type="button"
                  style={{
                    cursor: "pointer",
                    fontSize: "11px",
                    color: theme.secondaryColour,
                    background: "none",
                    border: "none",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {hiddenImpactsCount === allImpacts?.length
                    ? `Show (${hiddenImpactsCount})`
                    : `+ ${hiddenImpactsCount} more`}
                </button>
              }
            >
              {
                <div>
                  <H5>All {t("generic.impacts")}</H5>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "4px",
                    }}
                  >
                    {allImpacts.map((i: IdeaImpactDisplayMeasure) => (
                      <Popup
                        key={i.impactId}
                        position="right center"
                        trigger={
                          <Label
                            basic
                            color={
                              i.ownerImpact?.effect === "cost"
                                ? "red"
                                : i.ownerImpact?.effect === "saving"
                                  ? "green"
                                  : "grey"
                            }
                            size="small"
                            style={{
                              width: "100%",
                              margin: 0,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  overflow: "hidden",
                                }}
                              >
                                <Icon
                                  icon={getImpactIconName(i.ownerImpact?.type)}
                                />
                                <div
                                  style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {annualImpactValue(i)}
                                </div>
                              </div>
                              {i.choice?.weight !== undefined ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <Icon
                                    icon={CornerLeftUpIcon}
                                    style={{ marginLeft: 4, marginRight: 2 }}
                                  />
                                  {i.choice?.weight || 0}/
                                  {i.ownerImpact?.maxWeight || 0}
                                </div>
                              ) : null}
                            </div>
                          </Label>
                        }
                      >
                        {generateImpactText(i, t)}
                      </Popup>
                    ))}
                  </div>
                </div>
              }
            </Popup>
          ) : null}
        </div>
      </div>
      {pinnedImpacts.length !== 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "4px",
          }}
        >
          {pinnedImpacts.map((i: IdeaImpactDisplayMeasure) => (
            <Popup
              key={i.impactId}
              position="right center"
              trigger={
                <Label
                  basic
                  color={
                    i.ownerImpact?.effect === "cost"
                      ? "red"
                      : i.ownerImpact?.effect === "saving"
                        ? "green"
                        : "grey"
                  }
                  size="small"
                  style={{ width: "100%", margin: 0, overflow: "hidden" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        overflow: "hidden",
                      }}
                    >
                      <Icon icon={getImpactIconName(i.ownerImpact?.type)} />
                      <div
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: "11px",
                        }}
                      >
                        {annualImpactValue(i)}
                      </div>
                    </div>
                    {i.choice?.weight !== undefined ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Icon
                          icon={CornerLeftUpIcon}
                          style={{ marginLeft: 4, marginRight: 2 }}
                        />
                        {i.choice?.weight || 0}/{i.ownerImpact?.maxWeight || 0}
                      </div>
                    ) : null}
                  </div>
                </Label>
              }
            >
              {generateImpactText(i, t)}
            </Popup>
          ))}
        </div>
      ) : null}
    </div>
  );
};
